import React, { useEffect } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { GraphType } from '@components-clean/central-monitoring/panels/panel-constants';
import Loading from '@components-clean/core/loading/loading';
import PanelFrame from '@components-clean/core/panel-frame';
import { useStudyDataFilterContext } from '@contexts-clean/study-data-filter-context';
import { CM2Dataset } from '@interfaces/central-monitoring';
import { Column, SeriesDataDonut, SeriesDataHeatMap, StackedChartData } from '@interfaces-clean/panels.interface';
import { PanelModelType, PanelTheme, PanelType } from 'types/graphql-global-types';
import { GET_PANEL } from '@queries/central-monitoring';
import {
    panel,
    panelVariables,
    getCm2PanelsDataview_OpraQueries_cm2panels_Panel_graph_PanelGraph_series_PanelGraphSeries as PanelGraphSeries,
} from '@queries/generated/central-monitoring.generated';
import { useGetPanelData } from '@apis/bff-api/central-monitoring';

type DummyPanelFrameProps = {
    studyRef: string;
    dataset: CM2Dataset;
    panelRef: string;
    dataViewRef: string;
    panelType: PanelType;
    graphType: GraphType;
    modelType: PanelModelType;
    theme: PanelTheme;
};

export const DummyPanelFrame = ({
    studyRef,
    dataset,
    panelRef,
    dataViewRef,
    graphType,
    modelType,
    panelType,
    theme,
}: DummyPanelFrameProps) => {
    const { sessionId, filtersChanged$ } = useStudyDataFilterContext();
    const {
        isMutating,
        trigger,
        data: panelData,
        error,
    } = useGetPanelData(studyRef, dataset.id as number, panelRef, dataViewRef, sessionId);

    const [loadPanel, { loading, data: panelConfig }] = useLazyQuery<panel, panelVariables & { timestamp: string }>(
        GET_PANEL,
    );

    const triggerRequest = () => {
        trigger().catch((err) => window.console.log(err));
    };

    useEffect(() => {
        dataset?.id && triggerRequest();
        dataset?.created &&
            loadPanel({ variables: { panelId: panelRef, timestamp: dataset.created } }).catch((err) =>
                window.console.error(err),
            );
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [dataset]);

    useEffect(() => {
        triggerRequest();
        // Subscribe to the filter changed event emitted by the Filter UI, and re-trigger the get
        const subscription = filtersChanged$.subscribe(() => {
            triggerRequest();
        });

        // Return a function that unsubscribes from the observable
        return () => subscription.unsubscribe();
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, []);

    if (isMutating || loading) return <Loading />;

    switch (panelType) {
        case PanelType.GRAPH:
            if (graphType === 'DONUT')
                return (
                    <PanelFrame
                        data-testid="test-id"
                        title="V2 Donut Chart"
                        showLegend={false}
                        panelType={PanelType.GRAPH}
                        graphType="DONUT"
                        panelData={!error ? (panelData?.donut?.donutForStudy as SeriesDataDonut[]) || [] : []}
                        theme={theme}
                    />
                );
            if (graphType === 'COLUMN')
                return (
                    <PanelFrame
                        data-testid="test-id"
                        title="V2 Column Chart"
                        showLegend
                        panelType={PanelType.GRAPH}
                        graphType="COLUMN"
                        panelData={
                            {
                                studyData: !error ? panelData?.column?.columnForStudy || [] : [],
                                plotTitle: 'My plot title',
                                xTitle: 'xAxis title',
                                yTitle: 'yAxis Title',
                            } as Column
                        }
                        theme={theme}
                    />
                );

            if (graphType === 'HEATMAP') {
                return (
                    <PanelFrame
                        data-testid="test-id"
                        title={panelConfig?.panel?.graph?.plotTitle || ''}
                        showLegend
                        panelType={PanelType.GRAPH}
                        graphType="HEATMAP"
                        panelData={
                            !error
                                ? ({
                                      heatmapForIds: panelData?.heatMap?.heatMapForIds || {},
                                      maxValue: panelData?.heatMap?.maxValue || undefined,
                                      order: panelData?.heatMap?.order || undefined,
                                  } as SeriesDataHeatMap)
                                : {
                                      heatmapForIds: {},
                                  }
                        }
                        xAxisTitle={panelConfig?.panel?.graph?.xTitle || ''}
                        yAxisTitle={panelConfig?.panel?.graph?.yTitle || ''}
                        theme={theme}
                    />
                );
            }

            if (graphType === 'SCATTERPLOTLINE') {
                return (
                    <PanelFrame
                        data-testid="test-id"
                        title={panelConfig?.panel?.graph?.plotTitle || ''}
                        showLegend
                        panelType={PanelType.GRAPH}
                        graphType="SCATTERPLOTLINE"
                        thresholdType={panelConfig?.panel?.graph?.lineThresholdType}
                        panelData={{
                            ...panelData?.line,
                        }}
                        xAxisTitle={panelConfig?.panel?.graph?.xTitle || ''}
                        yAxisTitle={panelConfig?.panel?.graph?.yTitle || ''}
                        seriesConfig={{
                            type: panelConfig?.panel?.graph?.seriesType,
                            graphSeries: panelConfig?.panel?.graph?.series as PanelGraphSeries[] | null | undefined,
                        }}
                        theme={theme}
                    />
                );
            }
            if (graphType === 'STACKEDCOLUMN') {
                return (
                    <PanelFrame
                        data-testid="test-id"
                        title={panelConfig?.panel?.graph?.plotTitle || ''}
                        showLegend
                        panelType={PanelType.GRAPH}
                        graphType="STACKEDCOLUMN"
                        panelData={
                            !error
                                ? ({
                                      series: panelData?.stackedColumn?.columns,
                                      xCategories: panelData?.stackedColumn?.categories,
                                  } as StackedChartData)
                                : ({
                                      series: [],
                                      xCategories: [],
                                  } as StackedChartData)
                        }
                        xAxisTitle={panelConfig?.panel?.graph?.xTitle || ''}
                        yAxisTitle={panelConfig?.panel?.graph?.yTitle || ''}
                        theme={theme}
                    />
                );
            }
            return <div>INVALID GRAPH TYPE</div>;
        case PanelType.MODEL:
            switch (modelType) {
                case PanelModelType.TRI_OPERATIONAL:
                    return (
                        <PanelFrame
                            data-testid="test-id"
                            title={panelConfig?.panel?.graph?.plotTitle || ''}
                            showLegend
                            panelType={panelType}
                            modelConfig={{
                                type: modelType,
                                threshold1Type: panelConfig?.panel?.modelParams?.triThreshold1Type,
                                threshold2Type: panelConfig?.panel?.modelParams?.triThreshold2Type,
                            }}
                            panelData={{
                                ...panelData?.triOp,
                            }}
                            xAxisTitle={panelConfig?.panel?.graph?.xTitle || ''}
                            yAxisTitle={panelConfig?.panel?.graph?.yTitle || ''}
                            theme={theme}
                        />
                    );
                default:
                    return <div>INVALID GRAPH TYPE</div>;
            }
        default:
            return <div>INVALID GRAPH TYPE</div>;
    }
};
